import {
  NetworkError,
  deepCamelCase,
  deepSnakeCase,
  Select,
  intoSnakeCase,
} from '../fetchers/_helpers';
import { UserFields, User } from '../models/components/schemas/user/User';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetUserFields = readonly UserFields[];

/** Options for GET /users/{user_id} */
export type GetUserOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.user+json */
  select: Paths;
  where: {
    /**
     * The ID of the user.
     */
    userId: number;
  };
};

/** Result type for GET /users/{user_id} */
export type GetUserResult<Paths extends readonly string[]> = Select<User, Paths>;

/**
 * Get the user
 * This method returns the authenticated user.
 */
export async function getUser<Paths extends GetUserFields>({
  baseUrl,
  select,
  where: { userId },
  ...options
}: GetUserOptions<Paths>): Promise<GetUserResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/users/${userId}?fields=${select.map(intoSnakeCase).join(',')}`,
    {
      ...options,
      method: 'GET',
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as GetUserResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}

export type DeleteUserFields = readonly string[];

/** Options for DELETE /users/{user_id} */
export type DeleteUserOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the user.
     */
    userId: number;
  };
};

/** Result type for DELETE /users/{user_id} */
export type DeleteUserResult<Paths extends readonly string[]> = unknown;

/**
 * Delete the user's account
 * This method deletes the Vimeo account of the authenticated user.
 */
export async function deleteUser<Paths extends DeleteUserFields>({
  baseUrl,
  where: { userId },
  ...options
}: DeleteUserOptions<Paths>): Promise<DeleteUserResult<Paths>> {
  let res = await fetch(`${baseUrl}/users/${userId}`, {
    ...options,
    method: 'DELETE',
  });

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as DeleteUserResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}

export type PatchUserFields = readonly UserFields[];

/** Options for PATCH /users/{user_id} */
export type PatchUserOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  /** Available fields on the response type, application/vnd.vimeo.user+json */
  select: Paths;
  where: {
    /**
     * The ID of the user.
     */
    userId: number;
  };

  /** Data serialized to body for PATCH /users/{user_id} */
  variables?: {
    /**
     * Whether to apply the preset to all of the user's videos.
     */

    applyToAll?: boolean;

    /**
     * Whether the authenticated user is available for hire.
     */

    availableForHire?: boolean;

    /**
     * The user's bio.
     * @example This is where you will find videos and news updates from the staff
     */

    bio?: string;

    /**
     * Whether the authenticated user can work remotely.
     */

    canWorkRemotely?: boolean;

    /**
     * The user's city.
     * @example Albany
     */

    city?: string;

    /**
     * A list of values describing the content in the user's videos. Find the full list in the [/contentratings](https://developer.vimeo.com/api/reference/videos#get_content_ratings) endpoint. You must provide a comma-separated list if you're using a query string or an array if you're using JSON.
     */

    contentFilter?: string[];

    /**
     * The user's country.
     * @example US
     */

    country?: string;

    /**
     * The ISO code of the user's country.
     * @example US
     */

    countryIsoCode?: string;

    /**
     * The authenticated user's gender.
     *
     * Option descriptions:
     *  * `f` - The user's preferred pronouns are she and her.
     *  * `m` - The user's preferred pronouns are he and him.
     *  * `n` - The user would rather not give preferred pronouns.
     *  * `o` - The user's preferred pronouns are they and them.
     *
     */

    gender?: `f` | `m` | `n` | `o`;

    /**
     * The latitude of the user's location.
     * @example 41.3850632
     */

    latitude?: number;

    /**
     * The user's custom Vimeo URL.
     * @example staff
     */

    link?: string;

    /**
     * The user's location.
     * @example New York City
     */

    location?: string;

    /**
     * The longitude of the user's location.
     * @example 41.3850632
     */

    longitude?: number;

    /**
     * Whether the user has opted in to receive marketing communications.
     */

    marketingOptIn?: boolean;

    /**
     * The user's display name.
     * @example Vimeo Staff
     */

    name?: string;

    /**
     * The user's neighborhood.
     * @example Brooklyn
     */

    neighborhood?: string;

    /**
     * The default password for all future videos that this user uploads. To use this field, the **videos.privacy.view** field must be `password`.
     * @example hunter1
     */

    password?: string;

    /**
     * The place ID of the user's location as provided by Google Maps.
     * @example ChIJ5TCOcRaYpB
     */

    placeId?: string;

    profilePreferences?: {
      /**
       * The authenticated user's choice of profile layout.
       *
       * Option descriptions:
       *  * `grid` - The grid layout.
       *  * `masonry` - The masonry layout.
       *
       */

      layout?: `grid` | `masonry`;

      /**
       * The status of the module that helps the user get started. This module is found on the video manager's logged-in home page.
       *
       * Option descriptions:
       *  * `dismissed` - The user has dismissed the module for getting started.
       *  * `not_started` - The user doesn't have an active module for getting started.
       *  * `started` - The user has started the module for getting started.
       *
       * @example dismissed
       */

      lihpGettingStartedStatus?: `dismissed` | `not_started` | `started`;

      /**
       * The authenticated user's profile type.
       *
       * Option descriptions:
       *  * `business_professional` - The user identifies as a business professional.
       *  * `creative_professional` - The user identifies as a creator.
       *  * `education` - The user identifies as an educator.
       *  * `organization` - The user identifies as a member of a large organization.
       *  * `other` - The user identifies as a member of a non-specified segment.
       *  * `personal` - The user identifies as an individual or student.
       *  * `smb` - The user identifies as a member of a small- or medium-sized business.
       *  * `viewer` - The user identifies as wanting to consume content.
       *
       */

      profileType?:
        | `business_professional`
        | `creative_professional`
        | `education`
        | `organization`
        | `other`
        | `personal`
        | `smb`
        | `viewer`;

      /**
       * Whether to add public videos by default to the user's profile.
       * @example true
       */

      shouldAutoAddVideos?: boolean;

      /**
       * Whether to show the tip about adding a video.
       * @example true
       */

      showAddVideoTip?: boolean;

      /**
       * Whether to show the modal for joining Vimeo Experts on the user's profile page.
       * @example true
       */

      showJoinVimeoExpertsModal?: boolean;

      /**
       * Whether to show the tip about the user's profile type.
       * @example true
       */

      showProfileTypeTip?: boolean;

      /**
       * Whether to show the rate data in the user's profile.
       * @example true
       */

      showRate?: boolean;

      /**
       * The type of user entity profile that is set by the user on checkout.
       *
       * Option descriptions:
       *  * `business` - The user has a business profile.
       *  * `not_available` - The user hasn't been assigned a profile entity.
       *  * `personal` - The user has a personal profile.
       *
       * @example personal
       */

      userEntity?: `business` | `not_available` | `personal`;
    };

    /**
     * The user's state.
     * @example New York
     */

    state?: string;

    /**
     * The ISO code of the user's state.
     * @example NY
     */

    stateIsoCode?: string;

    /**
     * The user's sub-locality.
     * @example Manhattan
     */

    subLocality?: string;

    videos?: {
      /**
       * Whether the user has automatic review link generation enabled by default.
       * @example true
       */

      autoGenerateReviewLink?: boolean;

      /**
       * Whether the automatically generated captions for the user's videos are enabled by default.
       */

      autoccDisplayEnabledByDefault?: boolean;

      /**
       * The URI of the default video embed preset.
       * @example /users/1234/presets/12345
       */

      embedPresetUri?: string;

      /**
       * Whether the stats for the user's videos are hidden by default.
       * @example true
       */

      hideStats?: boolean;

      /**
       * Whether the user wants to keep the original source files after upload.
       * @example true
       */

      keepSourceFiles?: boolean;

      /**
       * The [Creative Commons](http://creativecommons.org/licenses/) license that the user has set to be the default.
       * @example by-nc-nd
       */

      license?: string;

      /**
       * The URI of the default video page preset.
       * @example /users/1234/page_presets/23456
       */

      pagePresetUri?: string;

      privacy?: {
        /**
         * Whether the user can add videos to showcases, channels, or groups by default.
         * @example true
         */

        add?: boolean;

        /**
         * Who can comment on the user's video uploads by default.
         *
         * Option descriptions:
         *  * `anybody` - Anyone can comment.
         *  * `contacts` - Only the user's contacts can comment.
         *  * `nobody` - No one can comment.
         *
         */

        comments?: `anybody` | `contacts` | `nobody`;

        /**
         * Whether the user can download videos. This value becomes the default download setting for all future videos that the user uploads.
         * @example true
         */

        download?: boolean;

        /**
         * The privacy for the user's embedded videos. The `whitelist` value enables you to define all valid embeddable domains. See our [Interacting with Videos](https://developer.vimeo.com/api/guides/videos/interact#set-off-site-privacy) guide for details on adding and removing domains.
         *
         * Option descriptions:
         *  * `private` - The videos can't be embedded on any domain.
         *  * `public` - The videos can be embedded on any domain.
         *  * `whitelist` - The videos can be embedded on the specified domains only.
         *
         */

        embed?: `private` | `public` | `whitelist`;

        /**
         * An array of the user's default list of valid domains for embedding videos.
         */

        embedAllowedDomains?: string[];

        /**
         * Who can access the user's videos by default.
         *
         * Option descriptions:
         *  * `anybody` - Anyone can access the videos. This privacy setting appears as `Public` on the Vimeo front end.
         *  * `contacts` - Only the user's contacts can access the videos. _This field is deprecated._
         *  * `disable` - The videos are disabled. This privacy setting appears as `Hide from Vimeo` on the Vimeo front end.
         *  * `nobody` - No one can access the videos. This privacy setting appears as `Private` on the Vimeo front end.
         *  * `password` - Only those with the password can access the videos.
         *  * `unlisted` - The videos are unlisted.
         *  * `users` - Only other Vimeo members can access the videos. _This field is deprecated._
         *
         */

        view?: `anybody` | `contacts` | `disable` | `nobody` | `password` | `unlisted` | `users`;
      };

      /**
       * An array of the user's default content ratings.
       *
       * Option descriptions:
       *  * `drugs` - The video contains drug or alcohol use.
       *  * `language` - The video contains profanity or sexually suggestive content.
       *  * `nudity` - The video contains nudity.
       *  * `safe` - The video is suitable for all audiences.
       *  * `unrated` - The video hasn't been rated.
       *  * `violence` - The video contains violent or graphic content.
       *
       */

      rating?: (`drugs` | `language` | `nudity` | `safe` | `unrated` | `violence`)[];
    };
  };
};

/** Result type for PATCH /users/{user_id} */
export type PatchUserResult<Paths extends readonly string[]> = Select<User, Paths>;

/**
 * Edit the user's account
 * This method edits the Vimeo account of the authenticated user.
 */
export async function patchUser<Paths extends PatchUserFields>({
  baseUrl,
  select,
  variables,
  where: { userId },
  ...options
}: PatchUserOptions<Paths>): Promise<PatchUserResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/users/${userId}?fields=${select.map(intoSnakeCase).join(',')}`,
    {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(deepSnakeCase(variables)),
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as PatchUserResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}
